import { useDispatch, useSelector } from 'react-redux';
import { Box, Grid, Tooltip, Typography, IconButton, Icon, Button } from '@mui/material';
import HelpIcon from '@mui/icons-material/Help';
import IncomeIcon from '../../../icons/Income.svg';
import CheckMarkIcon from '../../../icons/CheckMark.svg';
import IncompleteIcon from '../../../icons/IncompleteIcon.svg';
import EditIcon from '../../../icons/editIcon.svg';
import DeleteIcon from '../../../icons/deleteIcon.svg';
import { selectAuthState } from '../../../shared/sagas/auth/authSlice';
import { applicationStyles } from './CustomStyles';
import NumberFormat from 'react-number-format';
import DeleteModal from './DeleteModal';
import SpinnerModal from '../../../shared/Modal/SpinnerModal';
import { EmploymentStatusEnum} from '@inosio/payments-sdk';
import { useHistory, useRouteMatch } from 'react-router';
import {
  clearCreateEmploymentIncomeRecord,
  createEmploymentIncomeRecord,
  getDeleteEmploymentIncomeRecord,
  getEmploymentIncomeRecords,
  selectApplicantsState,
} from '../sagas/applicants/applicantsSlice';
import { selectPropertyGroupDetailsState } from '../../../shared/sagas/propertyGroups/propertyGroupsSlice';
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '../../../pmui/transactions/Transactions';
import { ApplicationSectionProps } from '../validators/ApplicantValidators';
import SpinnerWithNoConfirmation from '../../../shared/Modal/spinnerWithNoConfirmation';

export default function EmploymentDashboard(props: ApplicationSectionProps) {
  const query = useQuery();
  const hasValidationQueryParam = !!query.get('validation');
  const classes = applicationStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  let { url } = useRouteMatch();
  const { ipaUser } = useSelector(selectAuthState);
  const {
    EmploymentIncomeRecords,
    isDeletingEmploymentIncomeRecordSuccess,
    isCreatingEmploymentIncomeRecordSuccess,
    createdEmploymentIncomeRecord,
    isFetchingEmploymentIncomeRecordsSuccess,
    isFetchingEmploymentIncomeRecordsFailed,
  } = useSelector(selectApplicantsState);
  const { propertyGroupDetails, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed } = useSelector(selectPropertyGroupDetailsState);

  //local states
  const [showLocalSpinner, setShowLocalSpinner] = useState<boolean>(true);
  const [localError, setLocalError] = useState<string | null>(null);
  const [selectedEmploymentUuid, setSelectedEmploymentUuid] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isSpinnerModalOpen, setIsSpinnerModalOpen] = useState(false);
  const [spinnerStatus, setSpinnerStatus] = useState<string | null>(null);

  //fetch employment/income records
  useEffect(() => {
    if (ipaUser?.currentProfile.uuid) {
      dispatch(getEmploymentIncomeRecords(ipaUser?.currentProfile.uuid));
    }
  }, [dispatch, ipaUser]);

  //when delete is success, close spinner and re-fetch the employments
  useEffect(() => {
    if (isDeletingEmploymentIncomeRecordSuccess) {
      setSpinnerStatus('stop');
      if (ipaUser?.currentProfile.uuid) dispatch(getEmploymentIncomeRecords(ipaUser?.currentProfile.uuid));
    }
  }, [dispatch, ipaUser, isDeletingEmploymentIncomeRecordSuccess]);

  //when they click create new employment, api request succeeds, listen to this
  useEffect(() => {
    if (isCreatingEmploymentIncomeRecordSuccess && createdEmploymentIncomeRecord?.Uuid) {
      setSelectedEmploymentUuid(createdEmploymentIncomeRecord?.Uuid);
      dispatch(clearCreateEmploymentIncomeRecord());
      history.push(`${url}/${createdEmploymentIncomeRecord?.Uuid}`);
    }
  }, [isCreatingEmploymentIncomeRecordSuccess, createdEmploymentIncomeRecord]);

  //show or hide spinner depending on what data has been loaded.
  useEffect(() => {
    if (isFetchingEmploymentIncomeRecordsSuccess && isFetchingPropertyGroupDetailsSuccess) {
      //life is good
      setShowLocalSpinner(false);
      setLocalError(null);
    } else if (isFetchingEmploymentIncomeRecordsFailed || isFetchingPropertyGroupDetailsFailed) {
      //life is bad
      setShowLocalSpinner(false);
      setLocalError('There was an error loading the employments information. Try refreshing the page.');
    } else {
      setShowLocalSpinner(true);
    }
  }, [isFetchingEmploymentIncomeRecordsSuccess, isFetchingEmploymentIncomeRecordsFailed, isFetchingPropertyGroupDetailsSuccess, isFetchingPropertyGroupDetailsFailed]);

  const handleConfirmDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setIsSpinnerModalOpen(true);
    dispatch({
      type: getDeleteEmploymentIncomeRecord.type,
      payload: {
        applicantUuid: ipaUser?.currentProfile.uuid,
        employmentUuid: selectedEmploymentUuid,
      },
    });
  };

  const handleCloseSpinnerModal = useCallback(() => {
    setIsSpinnerModalOpen(false);
  }, []);

  const handleCreateNewEmployment = () => {
    dispatch(
      createEmploymentIncomeRecord({
        applicantUuid: ipaUser?.currentProfile.uuid,
        employmentDto: {},
      }),
    );
  };

  return (
    <Box width="100%" minHeight="60vh">
      {showLocalSpinner ? (
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 'inherit', alignItems: 'center' }}>
          <SpinnerWithNoConfirmation />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 'inherit', p: 4 }}>
          {localError && (
            <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', textAlign: 'center' }}>
              <Typography className={classes.globalText}>{localError}</Typography>
            </Box>
          )}
          <Typography className={classes.labelText}>{`Employment & Income History`}</Typography>

          <Typography sx={{ my: 2 }} className={classes.bodyText}>
            {`${propertyGroupDetails?.CompanyName || ''} requires ${
              propertyGroupDetails?.ApplicationsSettings?.ApplicationsConfigs?.ApplicationsEmploymentYearsRequired || 0
            } years of employment history.`}
          </Typography>

          <Grid container sx={{ mt: 2 }} spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', alignItems: 'center' }}>
              <Box className={classes.cardContainer}>
                <Box sx={{ px: 1.5 }} className={classes.cardTopContainer}>
                  <Box sx={{}}>
                    <Typography className={classes.cardLabelText}>
                      Employment & Income
                      <Tooltip title="Details about your income and how you will pay rent at your new home." placement="right">
                        <HelpIcon sx={{ ml: 0.5, fontSize: 17, color: '#C4C4C4', cursor: 'pointer' }} />
                      </Tooltip>
                    </Typography>
                  </Box>
                  <Button
                    type="button"
                    className={classes.wizardAddNewBtn}
                    onClick={() => {
                      handleCreateNewEmployment();
                    }}
                  >
                    <Typography className={classes.wizardAddNewBtnText} sx={{ margin: '0 10px' }}>
                      {`Add New`}
                    </Typography>
                  </Button>
                </Box>

                <Box sx={{ height: 280, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                  {EmploymentIncomeRecords?.length ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '1px',
                        height: '100%',
                        overflowY: 'auto',
                        '&::-webkit-scrollbar': {
                          width: 7,
                        },
                        '&::-webkit-scrollbar-track': {
                          boxShadow: `inset 0 0 6px rgba(0, 0, 0, 0.3)`,
                          borderRadius: '24px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          backgroundColor: 'darkgrey',
                          borderRadius: '24px',
                        },
                      }}
                    >
                      {EmploymentIncomeRecords.map((employment) => {
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', px: 2, py: 1, borderBottom: '1.5px solid #F1F0F5' }} key={employment.Uuid}>
                            <Box sx={{ width: '100%', pr: 6 }}>
                              <Grid container>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography className={classes.cardBodyText}>
                                    {props.Validator.IsEntireObjectComplete(employment) ? (
                                      <img
                                        style={{
                                          width: '12px',
                                          height: '9px',
                                          marginRight: '10px',
                                        }}
                                        src={CheckMarkIcon}
                                        alt="Completed"
                                      />
                                    ) : (
                                      <img
                                        style={{
                                          width: '12px',
                                          height: '16px',
                                          marginRight: '10px',
                                        }}
                                        src={IncompleteIcon}
                                        alt="Incomplete"
                                      />
                                    )}
                                    {employment.Status === EmploymentStatusEnum.Employed
                                      ? `${employment.Status} ${employment.CompanyName || ''} ${employment.Title || ''}`
                                      : employment.Status === EmploymentStatusEnum.SelfEmployed
                                      ? `${employment.Status} ${employment.CompanyName || ''}`
                                      : `${employment.Status}`}
                                  </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
                                  <Typography className={classes.cardBodyText}>
                                    <NumberFormat
                                      thousandsGroupStyle="thousand"
                                      value={employment.MonthlySalary || employment.OtherIncomeAmount}
                                      prefix="$"
                                      suffix="/month"
                                      decimalSeparator="."
                                      displayType="text"
                                      type="text"
                                      thousandSeparator={true}
                                      allowNegative={true}
                                    />
                                  </Typography>
                                </Grid>
                              </Grid>
                            </Box>
                            <Box sx={{ position: 'absolute', right: 16, height: '100%', display: 'flex', alignItems: 'center' }}>
                              <IconButton
                                onClick={() => {
                                  setSelectedEmploymentUuid(employment.Uuid);
                                  setIsDeleteModalOpen(true);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={DeleteIcon}
                                    alt="Delete"
                                  />
                                </Icon>
                              </IconButton>
                              <IconButton
                                onClick={() => {
                                  setSelectedEmploymentUuid(employment.Uuid);
                                  history.push(`${url}/${employment.Uuid}${hasValidationQueryParam ? '?validation=true' : ''}`);
                                }}
                                sx={{ p: 0.25, mr: '-2px' }}
                              >
                                <Icon sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                  <img
                                    style={{
                                      cursor: 'pointer',
                                      width: '14px',
                                      height: '14px',
                                    }}
                                    src={EditIcon}
                                    alt="Edit"
                                  />
                                </Icon>
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box sx={{ height: 170, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                      <img
                        style={{
                          width: '94px',
                          height: '94px',
                        }}
                        src={IncomeIcon}
                        alt="add income"
                      />
                    </Box>
                  )}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {isSpinnerModalOpen && (
        <SpinnerModal
          width="472px"
          height="523px"
          fastSpinner={true}
          openSpinnerModal={isSpinnerModalOpen}
          handleCloseSpinnerModal={handleCloseSpinnerModal}
          spinnerStatus={spinnerStatus}
          closeCancel={() => {
            setIsSpinnerModalOpen(false);
            setSpinnerStatus(null);
          }}
        />
      )}
      {isDeleteModalOpen && <DeleteModal open={isDeleteModalOpen} confirmCallback={handleConfirmDeleteModal} closeCallback={() => setIsDeleteModalOpen(false)} />}
    </Box>
  );
}
